/**
 * Settings -> Telefon -> the menu a caller hears before anybody is rung.
 *
 * Its own tab under Inbound rather than a block inside it, because it is the
 * one page here that changes what a customer meets FIRST. Everything else on
 * the inbound tab decides what happens to a call; this decides whether the call
 * is answered by a question.
 *
 * THREE THINGS THIS CARD HAS TO SAY OUT LOUD, because none of them can be found
 * by ringing the number:
 *
 *  1. The switch on with no question written is not a menu - it is eight
 *     seconds of silence and then a normal ring. The server refuses to run it
 *     (`isInboundMenuEnabled`), and `menuPlays` is that same answer.
 *  2. Twilio has no Estonian voice, so a question typed in Estonian needs a
 *     recording. The clip row below offers to make one.
 *  3. The keys are FIXED for the language question - one Estonian, two Russian,
 *     three English - because a recording made last month cannot be re-made
 *     because a list was reordered. The card prints them rather than letting
 *     somebody guess from the order of the tabs.
 */
import { useEffect, useState } from "react";
import { ListTree, RefreshCw, Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getTelephonySettings,
  saveTelephonySettings,
  type CallMenuDepartment,
  type NoticeVariant,
  type TelephonyStatus,
} from "../../../../lib/calls";
import { getDepartments } from "../../../../lib/support";
import { PopHost } from "../../../shell/Pop";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, FieldGroup, Note, Pill, SaveRow, ToggleRow } from "../panel/fields";
import { Btn, StatusPill } from "../../../ui";
import { MenuDepartmentRows } from "./MenuDepartmentRows";
import { NoticeVariantFields } from "./NoticeVariantFields";

const EMPTY: NoticeVariant = { audioUrl: "", text: "" };

/** Mirrors `MAX_MENU_DEPARTMENTS` on the server. Three is a spoken sentence. */
const MAX_DEPARTMENTS = 3;

export function MenuCard() {
  const t = useT();
  const [status, setStatus] = useState<TelephonyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [enabled, setEnabled] = useState(false);
  const [askLanguage, setAskLanguage] = useState(true);
  const [timeoutSec, setTimeoutSec] = useState("6");
  const [departments, setDepartments] = useState<CallMenuDepartment[]>([]);
  const [languagePrompt, setLanguagePrompt] = useState<NoticeVariant>(EMPTY);
  const [departmentPrompt, setDepartmentPrompt] = useState<NoticeVariant>(EMPTY);

  // The departments this desk actually files tickets under, for the
  // suggestions. Read beside the settings rather than lazily: a suggestion list
  // that arrives half a second late is one somebody has already typed past.
  const [inUse, setInUse] = useState<string[]>([]);

  function hydrate(data: TelephonyStatus) {
    setStatus(data);
    setEnabled(data.inbound.menu.enabled);
    setAskLanguage(data.inbound.menu.askLanguage);
    setTimeoutSec(String(data.inbound.menu.timeoutSec));
    setDepartments(data.inbound.menu.departments.map((row) => ({ ...row })));
    setLanguagePrompt({ ...data.inbound.menu.languagePrompt });
    setDepartmentPrompt({ ...data.inbound.menu.departmentPrompt });
  }

  useEffect(() => {
    getTelephonySettings()
      .then(hydrate)
      .catch(() => {})
      .finally(() => setLoading(false));
    // Silently on failure. No suggestions is a worse form, not a broken one:
    // the field is free text either way.
    getDepartments()
      .then((r) => setInUse(r.rows))
      .catch(() => {});
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      hydrate(
        await saveTelephonySettings({
          inbound: {
            menu: {
              enabled,
              askLanguage,
              timeoutSec: Number(timeoutSec),
              // Sent WHOLE, never row by row: a form holding two rows is saying
              // there are two now, not that the third should be left where it
              // was. Blank names are dropped by the server rather than refused,
              // so a half-typed row cannot 400 away the rest of the edit.
              departments,
              languagePrompt,
              departmentPrompt,
            },
          },
        }),
      );
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  const inboundOff = enabled && !(status?.inbound.ready ?? false);
  // The switch is on, the inbound line is up, and the menu STILL will not run.
  // The only cause is the first question having no wording - which sounds
  // exactly like the switch being off, only several seconds slower.
  const silent = enabled && (status?.inbound.ready ?? false) && !(status?.inbound.menuPlays ?? false);

  return (
    // The clip rows carry a `Pop` voice picker, and Settings mounts no `PopHost`
    // of its own - a `Pop` without one silently does not open.
    <PopHost>
      <IntegrationCard
        icon={ListTree}
        title={t("settings.menu.title")}
        desc={t("settings.menu.desc")}
        status={
          <ConnStatus
            ok={status?.inbound.menuPlays ?? false}
            okLabel={t("settings.menu.statusOn")}
            offLabel={t("settings.menu.statusOff")}
          />
        }
      >
        <form onSubmit={handleSave} className="space-y-5">
          <Note tone="info">{t("settings.menu.about")}</Note>
          {inboundOff ? <Note tone="bad">{t("settings.menu.needsInbound")}</Note> : null}
          {silent ? <Note tone="warn">{t("settings.menu.silent")}</Note> : null}

          <ToggleRow
            label={t("settings.menu.enable")}
            hint={t("settings.menu.enableHint")}
            checked={enabled}
            onChange={setEnabled}
          />

          <Field label={t("settings.menu.timeoutSec")} hint={t("settings.menu.timeoutSecHint")}>
            <Pill value={timeoutSec} onChange={setTimeoutSec} inputMode="numeric" placeholder="6" />
          </Field>

          <FieldGroup title={t("settings.menu.languageTitle")}>
            <ToggleRow
              label={t("settings.menu.askLanguage")}
              hint={t("settings.menu.askLanguageHint")}
              checked={askLanguage}
              onChange={setAskLanguage}
            />
            {askLanguage ? (
              <>
                {/* The keys are printed rather than left to be inferred: they
                    are fixed, and the wording somebody records has to name the
                    same numbers this reads. */}
                <Note tone="info">{t("settings.menu.languageKeys")}</Note>
                <NoticeVariantFields
                  slot="menu.language"
                  value={languagePrompt}
                  onChange={setLanguagePrompt}
                  savedClip={status?.clips["menu.language"]}
                  builtInAudio=""
                />
              </>
            ) : null}
          </FieldGroup>

          <FieldGroup title={t("settings.menu.departmentTitle")}>
            <MenuDepartmentRows
              rows={departments}
              onChange={setDepartments}
              suggestions={inUse}
              max={MAX_DEPARTMENTS}
            />
            {departments.length > 0 ? (
              <NoticeVariantFields
                slot="menu.department"
                value={departmentPrompt}
                onChange={setDepartmentPrompt}
                savedClip={status?.clips["menu.department"]}
                builtInAudio=""
              />
            ) : null}
          </FieldGroup>

          <SaveRow>
            <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
              {saveState === "saving" ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              {saveState === "saving" ? t("settings.inbound.saving") : t("settings.inbound.save")}
            </Btn>
            {saveState === "saved" ? <StatusPill tone="ok">{t("settings.inbound.saved")}</StatusPill> : null}
            {saveState === "error" ? (
              <StatusPill tone="lt">{t("settings.inbound.saveError")}</StatusPill>
            ) : null}
          </SaveRow>
        </form>
      </IntegrationCard>
    </PopHost>
  );
}
